import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, HeartCrack, Users, HelpCircle, Sparkles, BrainCircuit, Compass, Eye, Search, Lock, Star, MessageSquare, Calendar, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
const WHATSAPP_NUMBER = "5491126022502";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=Hola,%20quisiera%20solicitar%20información%20sobre%20una%20consulta%20de%20Tarot.`;
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: "-100px"
}} transition={{
  duration: 0.8,
  delay,
  ease: "easeOut"
}} data-id="element-0" data-mp="2mjpvpebsrcfa">
    {children}
  </motion.div>;
export function App() {
  return <div className="min-h-screen bg-deepBlack text-white overflow-x-hidden" data-id="element-1" data-mp="28te2td7e19n3">
      {/* Floating WhatsApp Button */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-whatsapp text-white p-4 rounded-full shadow-[0_4px_20px_rgba(37,211,102,0.4)] hover:bg-[#20bd5a] transition-all duration-300 hover:scale-110 flex items-center justify-center group" aria-label="Contactar por WhatsApp" data-id="element-2" data-mp="23utbk3a460as">
        <MessageCircle className="w-8 h-8" data-id="element-3" data-mp="3relw6la09qat" />
        <span className="absolute right-full mr-4 bg-white text-deepBlack px-3 py-1 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg" data-id="element-4" data-mp="4wv3njt0sf7">
          ¿Hablamos?
        </span>
      </a>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 pb-16 lg:pt-0 lg:pb-0" data-id="element-5" data-mp="38li7zffk45nl">
        <div className="absolute inset-0 z-0" data-id="element-6" data-mp="3dl4lzr37lpxg">
          <div className="absolute inset-0 bg-gradient-to-r from-deepBlack via-deepBlack/90 to-transparent z-10" data-id="element-7" data-mp="3diruonbka37q" />
          <div className="absolute inset-0 bg-gradient-to-t from-deepBlack via-transparent to-transparent z-10" data-id="element-8" data-mp="nxwupdpa56s0" />
          <img src="https://cdn.magicpatterns.com/uploads/bovYFXfW5HCRx7V6j5jCb3/ChatGPT_Image_12_jul_2026,_12_04_31_p.m..png" alt="Maestra de Tarot" className="w-full h-full object-cover object-center opacity-60" data-id="element-9" data-mp="3684hdq2uasw" />
        </div>

        <div className="container mx-auto px-6 relative z-20" data-id="element-10" data-mp="d3mkjc838xko">
          <div className="max-w-3xl" data-id="element-11" data-mp="85l0anyxes7m">
            <FadeIn data-id="element-12" data-mp="86mc4unrniqe">
              <div className="inline-flex items-center space-x-2 border border-gold/30 bg-gold/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8" data-id="element-13" data-mp="86cnbaegjhma">
                <Sparkles className="w-4 h-4 text-gold" data-id="element-14" data-mp="3bmjo995hn0rd" />
                <span className="text-gold text-sm font-medium tracking-wide uppercase" data-id="element-15" data-mp="3bo4l55f9yvse">Orientación Espiritual</span>
              </div>
            </FadeIn>
            
            <FadeIn delay={0.1} data-id="element-16" data-mp="3ljg0npyic717">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-semibold leading-tight mb-6 text-white" data-id="element-17" data-mp="3liedin47vu34">
                Obtén claridad sobre tu situación mediante una <span className="text-gold italic" data-id="element-18" data-mp="2sv0pjeihy8v5">consulta personalizada</span> de Tarot.
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-19" data-mp="18boiuhysu3uu">
              <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl font-light leading-relaxed" data-id="element-20" data-mp="1d47xzjd4x71r">
                Más de 20 años brindando orientación espiritual con absoluta confidencialidad. Descubre el camino hacia tu paz interior.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-21" data-mp="13cc0a9eyniy2">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-deepBlack bg-gold hover:bg-gold-light rounded-full transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:-translate-y-1" data-id="element-22" data-mp="1r3zg9waop9lk">
                <MessageCircle className="w-5 h-5 mr-2" data-id="element-23" data-mp="1m44nfn7cm9xv" />
                Solicitar Consulta por WhatsApp
              </a>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-24" data-mp="6qs5jsseji56">
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-25" data-mp="aqitzpughm6j">
                {["Atención personalizada", "Consulta privada", "Más de 20 años de experiencia", "Orientación espiritual profesional"].map((item, i) => <div key={i} className="flex items-center space-x-3 text-gray-300" data-id="element-26" data-mp="m5dz4ehblux0" data-mpi={i}>
                    <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0" data-id="element-27" data-mp="h6kkuju7lzvs" />
                    <span className="text-sm md:text-base" data-id="element-28" data-mp="c8j2600p42ku">{item}</span>
                  </div>)}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Is this for you? */}
      <section className="py-24 bg-gradient-to-b from-deepBlack to-[#161616]" data-id="element-29" data-mp="14wewwz6pfkez">
        <div className="container mx-auto px-6" data-id="element-30" data-mp="zxtvwxjmowph">
          <FadeIn data-id="element-31" data-mp="zwircviczkxb">
            <div className="text-center mb-16" data-id="element-32" data-mp="zw0o3exa46fw">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4" data-id="element-33" data-mp="vwrv4ca2ccle">¿Esta consulta es para ti?</h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full opacity-50" data-id="element-34" data-mp="3kob3mdmjo24r"></div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-35" data-mp="3gkwcaqt5nzc0">
            {[{
            icon: HeartCrack,
            text: "Problemas de pareja"
          }, {
            icon: Users,
            text: "Distanciamiento"
          }, {
            icon: HelpCircle,
            text: "Dudas sobre una relación"
          }, {
            icon: Sparkles,
            text: "Necesidad de orientación espiritual"
          }, {
            icon: BrainCircuit,
            text: "Confusión emocional"
          }, {
            icon: Compass,
            text: "Tomar decisiones importantes"
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-36" data-mp="2nvl476511dde" data-mpi={i}>
                <div className="bg-[#1A1A1A] border border-white/5 p-8 rounded-2xl hover:border-gold/30 transition-colors duration-300 group" data-id="element-37" data-mp="2nww9jae4et6m">
                  <div className="w-14 h-14 bg-darkViolet/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-38" data-mp="thlbty3w8tyu">
                    <item.icon className="w-7 h-7 text-gold" data-id="element-39" data-mp="ygww3h3gyhl6" />
                  </div>
                  <h3 className="text-xl font-medium text-white/90" data-id="element-40" data-mp="21sdhwbwls5kx">{item.text}</h3>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* What you'll receive */}
      <section className="py-24 bg-[#161616] relative overflow-hidden" data-id="element-41" data-mp="2atto07gdxljc">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-42" data-mp="25sw5tuvdkrhu">
          <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-darkViolet/20 blur-[120px] rounded-full" data-id="element-43" data-mp="2kpn2hwdnzp3r"></div>
          <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] bg-gold/10 blur-[120px] rounded-full" data-id="element-44" data-mp="2fqtv8loz0uc1"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10" data-id="element-45" data-mp="2ph50qcoazbl1">
          <FadeIn data-id="element-46" data-mp="2phointdydzz2">
            <div className="text-center mb-16" data-id="element-47" data-mp="2pj9cfgsg1evv">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4" data-id="element-48" data-mp="33g4rpqzag2kd">¿Qué recibirás durante tu consulta?</h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full opacity-50" data-id="element-49" data-mp="2yj5z5cv9w3oa"></div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-50" data-mp="3i1jw9chzhnoc">
            {[{
            icon: Eye,
            title: "Claridad",
            desc: "Comprender mejor tu situación actual desde una perspectiva más amplia."
          }, {
            icon: Search,
            title: "Identificación",
            desc: "Identificar factores ocultos que pueden estar influyendo en tu vida."
          }, {
            icon: Sparkles,
            title: "Orientación",
            desc: "Recibir orientación precisa basada en la lectura profunda de las cartas del Tarot."
          }, {
            icon: Lock,
            title: "Confidencialidad",
            desc: "Resolver tus dudas en un ambiente estrictamente privado, seguro y confidencial."
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-51" data-mp="2gf2nmvhg7ezr" data-mpi={i}>
                <div className="flex items-start space-x-6 bg-deepBlack/50 backdrop-blur-sm p-8 rounded-3xl border border-white/5 hover:bg-deepBlack/80 transition-colors" data-id="element-52" data-mp="2kge4ocm9arwc">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-53" data-mp="30t13o0tbswtn">
                    <item.icon className="w-6 h-6 text-gold" data-id="element-54" data-mp="2qwxvad7vs1y9" />
                  </div>
                  <div data-id="element-55" data-mp="2ly4of3n3n972">
                    <h3 className="text-xl font-serif font-semibold mb-2 text-gold" data-id="element-56" data-mp="2lywa9mqw9sk5">{item.title}</h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-57" data-mp="2hwrgmo0g4vxi">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-deepBlack" data-id="element-58" data-mp="287hppbhc9jaz">
        <div className="container mx-auto px-6" data-id="element-59" data-mp="237mzzb9auzri">
          <FadeIn data-id="element-60" data-mp="238y1f4f9vvfg">
            <div className="text-center mb-12" data-id="element-61" data-mp="239g68pg03hyz">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4" data-id="element-62" data-mp="1y91w91uu9xyb">Conoce cómo funciona la consulta</h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full opacity-50" data-id="element-63" data-mp="1u7zvuhp6rfur"></div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-64" data-mp="9ocu8p5od85c">
            <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(75,30,109,0.2)] border border-white/10 relative bg-black aspect-video" data-id="element-65" data-mp="9r8xmt0akp16">
              <video className="w-full h-full object-cover" autoPlay loop playsInline controls={true} data-id="element-66" data-mp="iq44m7b5u35j">
                <source src="https://cdn.magicpatterns.com/uploads/e7BMNGvPhiYWHS8m6wa1cy/copy-0d429bff-9508-4ee3-afcb-8746d4891120_VKb1j0Ru.mp4" type="video/mp4" data-id="element-67" data-mp="nnw2rj2eeli6" />
                Tu navegador no soporta el elemento de video.
              </video>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-68" data-mp="3w3pbhbqhmsoo">
            <div className="mt-12 text-center" data-id="element-69" data-mp="3w2nxozi37s39">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-10 py-5 text-xl font-medium text-white bg-whatsapp hover:bg-[#20bd5a] rounded-full transition-all duration-300 hover:shadow-[0_0_30px_rgba(37,211,102,0.3)] hover:-translate-y-1" data-id="element-70" data-mp="4quwlux3yfcn">
                <MessageCircle className="w-6 h-6 mr-3" data-id="element-71" data-mp="2f6by7lxdgrpo" />
                Hablar por WhatsApp
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#161616]" data-id="element-72" data-mp="2p5uftvpt81ek">
        <div className="container mx-auto px-6" data-id="element-73" data-mp="2u4e628bfrzcq">
          <FadeIn data-id="element-74" data-mp="2u3vy4efevwoz">
            <div className="text-center mb-16" data-id="element-75" data-mp="2u2bf9m2gm26y">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4" data-id="element-76" data-mp="1whf2ddmbtr5c">¿Por qué tantas personas confían en nosotros?</h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full opacity-50" data-id="element-77" data-mp="21g8gn89ftm6k"></div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-78" data-mp="26cqju3p4dcgs">
            {[{
            name: "María C.",
            text: "Estaba pasando por un momento de mucha incertidumbre en mi matrimonio. La consulta me dio la claridad que necesitaba para tomar decisiones desde el amor y no desde el miedo."
          }, {
            name: "Carlos R.",
            text: "Nunca había consultado el Tarot, pero la profesionalidad y el respeto con el que me trataron me dio mucha confianza. Me ayudó a entender cosas de mí mismo que no veía."
          }, {
            name: "Ana P.",
            text: "Un espacio lleno de luz. La maestra fue muy asertiva y me transmitió mucha paz. Salí de la consulta sintiéndome mucho más tranquila y enfocada en mi camino."
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-79" data-mp="3d2r3s4b1s6q5" data-mpi={i}>
                <div className="bg-deepBlack p-8 rounded-3xl border border-white/5 relative" data-id="element-80" data-mp="389o4rgni452k">
                  <div className="flex space-x-1 mb-6" data-id="element-81" data-mp="347t2o9xqxnnq">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 text-gold fill-gold" data-id="element-82" data-mp="2z9hx4behhzbb" data-mpi={j} />)}
                  </div>
                  <p className="text-gray-300 mb-8 font-light italic leading-relaxed" data-id="element-83" data-mp="2u9vfzqvjbyx8">"{item.text}"</p>
                  <div className="flex items-center space-x-4" data-id="element-84" data-mp="2pajzmiz2yvg6">
                    <div className="w-12 h-12 bg-darkViolet rounded-full flex items-center justify-center text-gold font-serif font-bold text-lg" data-id="element-85" data-mp="2pd5136vgv9de">
                      {item.name.charAt(0)}
                    </div>
                    <div data-id="element-86" data-mp="26mro65pwj6du">
                      <p className="font-medium text-white" data-id="element-87" data-mp="26njk5hnev64n">{item.name}</p>
                      <p className="text-sm text-gray-500" data-id="element-88" data-mp="2ao3eyekihxnm">Consulta Privada</p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-deepBlack" data-id="element-89" data-mp="33bpi5lptv09y">
        <div className="container mx-auto px-6" data-id="element-90" data-mp="2plm1rbdmq0sf">
          <FadeIn data-id="element-91" data-mp="2pkugatdqxjfv">
            <div className="text-center mb-16" data-id="element-92" data-mp="2pldo3h9omrg6">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4" data-id="element-93" data-mp="2tmpfnsc49mr0">¿Cómo funciona?</h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full opacity-50" data-id="element-94" data-mp="3hc9zsek0aq1v"></div>
            </div>
          </FadeIn>

          <div className="max-w-5xl mx-auto" data-id="element-95" data-mp="3m4jvycbpsmcl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative" data-id="element-96" data-mp="3m5czl4fdmvqi">
              <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-white/10 -translate-y-1/2 z-0" data-id="element-97" data-mp="1amq9t7ozoalj"></div>
              
              {[{
              icon: MessageCircle,
              title: "Paso 1",
              desc: "Haz clic en WhatsApp"
            }, {
              icon: MessageSquare,
              title: "Paso 2",
              desc: "Cuéntanos tu situación"
            }, {
              icon: Calendar,
              title: "Paso 3",
              desc: "Agenda tu consulta"
            }, {
              icon: Sparkles,
              title: "Paso 4",
              desc: "Recibe orientación"
            }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-98" data-mp="wo8n9datl457" data-mpi={i}>
                  <div className="relative z-10 flex flex-col items-center text-center group" data-id="element-99" data-mp="wngsg4p1rafz">
                    <div className="w-20 h-20 bg-[#1A1A1A] border-2 border-gold/30 rounded-full flex items-center justify-center mb-6 group-hover:bg-gold transition-colors duration-300 group-hover:border-gold" data-id="element-100" data-mp="2d16yhgzq88s9">
                      <item.icon className="w-8 h-8 text-gold group-hover:text-deepBlack transition-colors duration-300" data-id="element-101" data-mp="howguw4gunwq" />
                    </div>
                    <h3 className="text-gold font-serif font-semibold mb-2" data-id="element-102" data-mp="qn8c5bga29vi">{item.title}</h3>
                    <p className="text-gray-300" data-id="element-103" data-mp="vn494qqk511k">{item.desc}</p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#161616]" data-id="element-104" data-mp="15k8vaqkekwid">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-105" data-mp="15j7fy9wctnuu">
          <FadeIn data-id="element-106" data-mp="1ab8rftlp7ajt">
            <div className="text-center mb-16" data-id="element-107" data-mp="1acs83lb9nnm4">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4" data-id="element-108" data-mp="1ed2pdu4ie0ga">Preguntas Frecuentes</h2>
              <div className="w-24 h-1 bg-gold mx-auto rounded-full opacity-50" data-id="element-109" data-mp="1jc6uo9obbe1c"></div>
            </div>
          </FadeIn>

          <div className="space-y-4" data-id="element-110" data-mp="1oagu4wh2l0sr">
            {[{
            q: "¿La consulta es privada?",
            a: "Absolutamente. Toda la información compartida durante la sesión es estrictamente confidencial. Tu privacidad es nuestra máxima prioridad."
          }, {
            q: "¿Necesito experiencia previa?",
            a: "No necesitas ninguna experiencia previa. La maestra te guiará durante todo el proceso y te explicará cada detalle de manera clara y sencilla."
          }, {
            q: "¿Cómo se realiza la consulta?",
            a: "Las consultas se realizan de forma online (videollamada o llamada de voz) para tu mayor comodidad, manteniendo la misma conexión y efectividad que una sesión presencial."
          }, {
            q: "¿Cuánto dura?",
            a: "Cada sesión está diseñada para brindarte el tiempo necesario sin prisas. Generalmente, una consulta completa dura entre 45 y 60 minutos."
          }, {
            q: "¿Cómo puedo reservar?",
            a: "Es muy sencillo. Solo tienes que hacer clic en cualquiera de los botones de WhatsApp de esta página, contarnos brevemente tu caso y coordinaremos el mejor horario para ti."
          }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} delay={i * 0.1} data-id="element-111" data-mp="y6qq4fo2pqpb" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-deepBlack relative overflow-hidden" data-id="element-112" data-mp="jhkt8ycpkckl">
        <div className="absolute inset-0 z-0" data-id="element-113" data-mp="og5laaht3pxy">
          <img src="https://cdn.magicpatterns.com/uploads/46t4Psxi1kQ9XxG85KHCdd/ChatGPT_Image_11_jun_2026,_11_15_26.png" alt="Cartas de Tarot" className="w-full h-full object-cover object-center opacity-20" data-id="element-114" data-mp="oeugq8gjee5s" />
          <div className="absolute inset-0 bg-gradient-to-b from-deepBlack via-deepBlack/80 to-deepBlack" data-id="element-115" data-mp="j9o8isqqu7d" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center max-w-4xl" data-id="element-116" data-mp="5htbcmj2mc1b">
          <FadeIn data-id="element-117" data-mp="1uxiyss8oggh2">
            <h2 className="text-5xl md:text-6xl font-serif font-semibold mb-8 text-white" data-id="element-118" data-mp="1ux1788wymqfh">
              Da el primer paso hacia una <span className="text-gold italic" data-id="element-119" data-mp="1zv2pwsqh4nqf">mayor claridad.</span>
            </h2>
          </FadeIn>
          
          <FadeIn delay={0.1} data-id="element-120" data-mp="3keoaiehxz9g2">
            <p className="text-xl text-gray-300 mb-12 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-121" data-mp="3ffbq00ejmegn">
              Si sientes que ha llegado el momento de comprender mejor tu situación y recibir orientación personalizada, estaremos encantados de atenderte.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-122" data-mp="3pbojax66eqav">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-12 py-6 text-xl font-medium text-white bg-whatsapp hover:bg-[#20bd5a] rounded-full transition-all duration-300 hover:shadow-[0_0_40px_rgba(37,211,102,0.4)] hover:-translate-y-1" data-id="element-123" data-mp="6yqn91ci8hlz">
              <MessageCircle className="w-7 h-7 mr-3" data-id="element-124" data-mp="bzmr5ewuw3re" />
              Hablar por WhatsApp
            </a>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] py-12 border-t border-white/5" data-id="element-125" data-mp="2dq7zapthq7vl">
        <div className="container mx-auto px-6" data-id="element-126" data-mp="2dsawbtt2919t">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-127" data-mp="1rvr5nzyq8fxn">
            <div className="mb-6 md:mb-0" data-id="element-128" data-mp="1wvdg61n67i2r">
              <span className="text-2xl font-serif text-gold font-semibold tracking-wider" data-id="element-129" data-mp="1wu2fiap0uqfv">TAROT<span className="text-white font-light" data-id="element-130" data-mp="21nww359bypaq">&</span>AMOR</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500" data-id="element-131" data-mp="26kdls3w6hbpx">
              <a href="#" className="hover:text-gold transition-colors" data-id="element-132" data-mp="18yzphzehegxt">Política de privacidad</a>
              <a href="#" className="hover:text-gold transition-colors" data-id="element-133" data-mp="1dy2j8np0h0qh">Términos y condiciones</a>
              <a href="#" className="hover:text-gold transition-colors" data-id="element-134" data-mp="1iv2paylv23hl">Aviso legal</a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/5 text-center text-sm text-gray-600" data-id="element-135" data-mp="1nwid2yffao3o">
            <p data-id="element-136" data-mp="2uazt1sycyryv">&copy; {new Date().getFullYear()} Tarot & Amor. Todos los derechos reservados.</p>
            <p className="mt-2 text-xs opacity-60" data-id="element-137" data-mp="2z8j5cnbhwuo6">Este servicio es exclusivamente para fines de entretenimiento y orientación personal. No sustituye asesoramiento médico, legal o psicológico profesional.</p>
          </div>
        </div>
      </footer>
    </div>;
}
function FAQItem({
  question,
  answer,
  delay
}: {
  question: string;
  answer: string;
  delay: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <FadeIn delay={delay} data-id="element-138" data-mp="3z1h0k8etu32">
      <div className="border border-white/10 rounded-2xl overflow-hidden bg-deepBlack/50 backdrop-blur-sm transition-colors hover:border-white/20" data-id="element-139" data-mp="3z9v2fg9r5fu">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-140" data-mp="3w4wxwsujq3tq">
          <span className="text-lg font-medium text-white/90 pr-8" data-id="element-141" data-mp="3m55zwh6s16ow">{question}</span>
          {isOpen ? <ChevronUp className="w-5 h-5 text-gold flex-shrink-0" data-id="element-142" data-mp="1pcw53qznbv7o" /> : <ChevronDown className="w-5 h-5 text-gold flex-shrink-0" data-id="element-143" data-mp="1pa9ybtkkxjqi" />}
        </button>
        <AnimatePresence data-id="element-144" data-mp="1kdt20c387z2c">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: "auto",
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: "easeInOut"
        }} data-id="element-145" data-mp="2c7chcdhdg8ag">
              <div className="px-6 pb-5 text-gray-400 leading-relaxed" data-id="element-146" data-mp="1xig1hu92pqza">
                {answer}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </FadeIn>;
}